import { useState, useCallback, useEffect } from 'react'
import { apiGet } from '../../../../../generic/Api_Functions'
import useMosInfo from '../../../common/provider/useMosInfo'
import { useSmartInterval } from '../../../../../generic/hooks/useSmartInterval'

/**
 * @param {unknown[]} incidences
 * @returns {object|null} The incidence with the most recent startDate.
 */
const getLatestIncidenceByStartDate = (incidences) => {
  if (!Array.isArray(incidences) || incidences.length === 0) return null

  let latest = null
  let latestTime = Number.NEGATIVE_INFINITY

  for (const item of incidences) {
    const time = new Date(item?.startDate).getTime()
    if (!Number.isNaN(time) && time >= latestTime) {
      latestTime = time
      latest = item
    }
  }

  return latest
}

/**
 * Hook to load and manage the active incidence for a device.
 * @returns {{
 *   incidence: Object|null,
 *   loadIncidence: Function,
 *   loading: boolean
 * }} The incidence state and loading functions.
 */
export const useIncidenceLoader = () => {
  const { device, setGlobalData } = useMosInfo()
  const [incidence, setIncidence] = useState(null)
  const [loading, setLoading] = useState(false)

  /**
   * @param {object|null} nextIncidence
   * @returns {void}
   */
  const setIncidenceState = useCallback((nextIncidence) => {
    setIncidence(nextIncidence)
    setGlobalData((prev) => ({ ...prev, incidence: nextIncidence }))
  }, [setGlobalData])

  /**
   * Fetches the active incidence list for the current device and stores the latest one.
   * @returns {Promise<void>}
   */
  const loadIncidence = useCallback(async () => {
    if (!device?.id) return

    setLoading(true)
    try {
      const res = await apiGet(`mos/incidences/${device.id}/${true}`)
      setIncidenceState(getLatestIncidenceByStartDate(res))
    } catch (e) {
      console.error('Error loading incidence:', e)
      setIncidenceState(null)
    } finally {
      setLoading(false)
    }
  }, [device?.id, setIncidenceState])

  useSmartInterval(loadIncidence, 2000, () => {})

  useEffect(() => {
    loadIncidence()
  }, [loadIncidence])

  return { incidence, loadIncidence, loading }
}
